/**
 * <dorahi-shoppable-stories> — behaviour for sections/dorahi-shoppable-stories.liquid.
 * Vanilla port of dorahi-main/components/homepage/ShoppableStories.tsx.
 *
 * Grid cards (VideoGridItem):
 *   - IntersectionObserver (threshold 0.5) plays/pauses the preview video
 *   - seeks to 0.1s on loadedmetadata so that frame acts as the poster
 *   - 3-second loop (timeupdate resets currentTime to 0)
 *   - iOS Low Power Mode bypass: on NotAllowedError, retry on the next
 *     window touchstart/click (once)
 *   - clicking a card opens the lightbox (the Shop Now link is excluded)
 *
 * Lightbox (Lightbox + LightboxSlide):
 *   - stacked-card swiper: transform/opacity/z-index computed per offset with
 *     the exact formulas from the tsx (mobile < 768px variant included)
 *   - active slide autoplays from 0, inactive slides pause behind a dimmer
 *   - tap active slide to play/pause (overlay play icon when paused)
 *   - mute + Picture-in-Picture buttons, Esc / arrow keys, swipe left/right,
 *     backdrop click to close, body scroll locked while open
 *
 * Carousel drag/snap/progress is handled by <dorahi-carousel> (dorahi-carousel.js).
 * Classes toggled here (hidden/flex/opacity-*) are safelisted or present in
 * the section markup, so Tailwind generates them.
 */
(function () {
  if (customElements.get('dorahi-shoppable-stories')) return;

  const SWIPE_THRESHOLD = 50;

  class DorahiShoppableStories extends HTMLElement {
    connectedCallback() {
      this.cards = Array.from(this.querySelectorAll('[data-story-item]'));
      this.lightbox = this.querySelector('[data-lightbox]');
      this.stage = this.querySelector('[data-lightbox-stage]');
      this.slides = Array.from(this.querySelectorAll('[data-lightbox-slide]'));
      this.currentIndex = null;
      this.isMuted = false;
      this.lightboxLoaded = false;

      this.onKeyDown = this.onKeyDown.bind(this);
      this.onResize = this.onResize.bind(this);

      this.cards.forEach((card) => this.setupCard(card));
      this.setupLightbox();

      window.addEventListener('resize', this.onResize);
    }

    disconnectedCallback() {
      window.removeEventListener('keydown', this.onKeyDown);
      window.removeEventListener('resize', this.onResize);
      if (this.observers) this.observers.forEach((io) => io.disconnect());
    }

    onResize() {
      // The tsx recomputes the isMobile stacking variant on resize.
      if (this.currentIndex !== null) this.renderSlides();
    }

    /* ------------------------------------------------------------- grid cards */

    setupCard(card) {
      const video = card.querySelector('[data-story-video]');
      const poster = card.querySelector('[data-story-poster]');
      if (!video) return;

      const state = { visible: false, restricted: false };

      const attemptPlay = () => {
        const promise = video.play();
        if (!promise || !promise.then) return;
        promise
          .then(() => {
            state.restricted = false;
          })
          .catch((err) => {
            // NotAllowedError is common in iOS Low Power Mode / Safari.
            if (err && err.name === 'NotAllowedError' && !state.restricted) {
              state.restricted = true;
              const onUserInteraction = () => {
                if (state.visible) {
                  video
                    .play()
                    .then(() => {
                      state.restricted = false;
                    })
                    .catch(() => {});
                }
              };
              // touchstart fires before scroll starts on iOS.
              window.addEventListener('touchstart', onUserInteraction, { once: true });
              window.addEventListener('click', onUserInteraction, { once: true });
            }
          });
      };

      // Seek to 0.1s immediately to use that frame as a poster.
      video.addEventListener('loadedmetadata', () => {
        try {
          video.currentTime = 0.1;
        } catch (e) {
          /* no-op */
        }
      });

      video.addEventListener('loadeddata', () => {
        video.classList.remove('opacity-0');
        video.classList.add('opacity-100');
        video.removeAttribute('poster');
        if (poster) poster.classList.add('hidden');
      });

      // 3-second loop logic.
      video.addEventListener('timeupdate', () => {
        if (state.visible && video.currentTime >= 3) {
          video.currentTime = 0;
          attemptPlay();
        }
      });

      const io = new IntersectionObserver(
        ([entry]) => {
          state.visible = entry.isIntersecting;
          if (state.visible) {
            attemptPlay();
          } else {
            video.pause();
            try {
              video.currentTime = 0.1;
            } catch (e) {
              /* no-op */
            }
          }
        },
        { threshold: 0.5 }
      );
      io.observe(video);
      this.observers = this.observers || [];
      this.observers.push(io);

      card.addEventListener('click', (event) => {
        // The Shop Now link stops propagation in the tsx and just navigates.
        if (event.target.closest('[data-shop-link]')) return;
        this.openLightbox(Number(card.dataset.storyIndex));
      });
    }

    /* --------------------------------------------------------------- lightbox */

    setupLightbox() {
      if (!this.lightbox || !this.stage) return;

      // Backdrop click closes (only when the stage itself is the target).
      this.stage.addEventListener('click', (event) => {
        if (event.target === this.stage) this.closeLightbox();
      });

      // Swipe left/right (min distance 50px, same as the tsx).
      let touchStartX = null;
      let touchEndX = null;
      this.stage.addEventListener(
        'touchstart',
        (event) => {
          touchEndX = null;
          touchStartX = event.targetTouches[0].clientX;
        },
        { passive: true }
      );
      this.stage.addEventListener(
        'touchmove',
        (event) => {
          touchEndX = event.targetTouches[0].clientX;
        },
        { passive: true }
      );
      this.stage.addEventListener('touchend', () => {
        if (touchStartX === null || touchEndX === null) return;
        const distance = touchStartX - touchEndX;
        if (distance > SWIPE_THRESHOLD) this.next();
        if (distance < -SWIPE_THRESHOLD) this.prev();
      });

      const prevButton = this.lightbox.querySelector('[data-lightbox-prev]');
      const nextButton = this.lightbox.querySelector('[data-lightbox-next]');
      if (prevButton)
        prevButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.prev();
        });
      if (nextButton)
        nextButton.addEventListener('click', (event) => {
          event.stopPropagation();
          this.next();
        });

      this.slides.forEach((slide, index) => {
        const video = slide.querySelector('[data-lightbox-video]');

        // Tap the active slide to play/pause.
        slide.addEventListener('click', () => {
          if (index !== this.currentIndex || !video) return;
          if (video.paused) {
            video.play().catch(() => {});
            this.setPlayingUI(slide, true);
          } else {
            video.pause();
            this.setPlayingUI(slide, false);
          }
        });

        const closeButton = slide.querySelector('[data-lightbox-close]');
        if (closeButton)
          closeButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.closeLightbox();
          });

        const muteButton = slide.querySelector('[data-lightbox-mute]');
        if (muteButton)
          muteButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.toggleMute();
          });

        const pipButton = slide.querySelector('[data-lightbox-pip]');
        if (pipButton)
          pipButton.addEventListener('click', (event) => {
            event.stopPropagation();
            this.togglePiP();
          });

        const chip = slide.querySelector('[data-lightbox-chip]');
        if (chip)
          chip.addEventListener('click', (event) => {
            event.stopPropagation();
          });
      });
    }

    openLightbox(index) {
      if (!this.lightbox || !this.slides.length) return;

      // Videos load once the lightbox is first opened (the React version only
      // mounts the slides at that point).
      if (!this.lightboxLoaded) {
        this.lightboxLoaded = true;
        this.slides.forEach((slide) => {
          const video = slide.querySelector('[data-lightbox-video]');
          if (video && !video.src && video.dataset.src) video.src = video.dataset.src;
        });
      }

      this.currentIndex = index;
      document.body.style.overflow = 'hidden';
      this.lightbox.classList.remove('hidden');
      window.addEventListener('keydown', this.onKeyDown);
      this.renderSlides(true);
    }

    closeLightbox() {
      if (!this.lightbox) return;
      this.currentIndex = null;
      this.lightbox.classList.add('hidden');
      document.body.style.overflow = 'unset';
      window.removeEventListener('keydown', this.onKeyDown);
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-lightbox-video]');
        if (video) video.pause();
      });
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
      }
    }

    next() {
      if (this.currentIndex === null) return;
      this.currentIndex = (this.currentIndex + 1) % this.slides.length;
      this.renderSlides(true);
    }

    prev() {
      if (this.currentIndex === null) return;
      this.currentIndex = (this.currentIndex - 1 + this.slides.length) % this.slides.length;
      this.renderSlides(true);
    }

    onKeyDown(event) {
      if (event.key === 'Escape') this.closeLightbox();
      if (event.key === 'ArrowRight') this.next();
      if (event.key === 'ArrowLeft') this.prev();
    }

    /**
     * Applies the stacked-card styles from LightboxSlide's useMemo.
     * Tailwind v3's -translate-x/y-1/2 classes live on the `transform`
     * property, so the -50% centering is folded into the inline transform
     * (v4 uses the standalone `translate` property, which composes on its own).
     */
    renderSlides(restart) {
      const length = this.slides.length;
      const isMobile = window.innerWidth < 768;

      this.slides.forEach((slide, index) => {
        let offset = index - this.currentIndex;
        if (offset > length / 2) offset -= length;
        else if (offset < -length / 2) offset += length;

        const absOffset = Math.abs(offset);
        const isActive = index === this.currentIndex;

        const scale = isMobile
          ? Math.max(0, 1 - absOffset * 0.2)
          : Math.max(0, 1 - absOffset * 0.1);
        const zIndex = 14 - absOffset;
        const opacity = absOffset > 4 ? 0 : 1;

        let xPercent = 0;
        if (absOffset > 0) {
          if (isMobile) {
            xPercent = 50 * absOffset + 15 * absOffset * (absOffset - 1);
          } else {
            // Desktop: tighter stack
            xPercent = 15 * absOffset;
          }
        }
        xPercent = xPercent * Math.sign(offset);

        slide.style.transform = `translate(-50%, -50%) translateX(${xPercent}%) scale(${scale})`;
        slide.style.opacity = String(opacity);
        slide.style.zIndex = String(zIndex);
        slide.style.pointerEvents = isActive ? 'auto' : 'none';
        slide.style.visibility = absOffset > 4 ? 'hidden' : 'visible';

        const video = slide.querySelector('[data-lightbox-video]');
        const dimmer = slide.querySelector('[data-lightbox-dimmer]');
        const ui = slide.querySelector('[data-lightbox-ui]');

        if (dimmer) {
          dimmer.classList.toggle('opacity-0', isActive);
          dimmer.classList.toggle('opacity-60', !isActive);
        }
        if (ui) {
          ui.classList.toggle('opacity-100', isActive);
          ui.classList.toggle('opacity-0', !isActive);
        }

        if (!video) return;
        video.muted = this.isMuted;

        if (isActive) {
          if (restart) {
            try {
              video.currentTime = 0;
            } catch (e) {
              /* no-op */
            }
          }
          this.setPlayingUI(slide, true);
          const promise = video.play();
          if (promise && promise.catch) {
            promise.catch(() => this.setPlayingUI(slide, false));
          }
        } else {
          video.pause();
          this.setPlayingUI(slide, false);
        }
      });
    }

    setPlayingUI(slide, isPlaying) {
      const overlay = slide.querySelector('[data-lightbox-play-overlay]');
      if (!overlay) return;
      overlay.classList.toggle('hidden', isPlaying);
      overlay.classList.toggle('flex', !isPlaying);
    }

    toggleMute() {
      this.isMuted = !this.isMuted;
      this.slides.forEach((slide) => {
        const video = slide.querySelector('[data-lightbox-video]');
        if (video) video.muted = this.isMuted;
        const unmutedIcon = slide.querySelector('[data-icon-unmuted]');
        const mutedIcon = slide.querySelector('[data-icon-muted]');
        if (unmutedIcon) unmutedIcon.classList.toggle('hidden', this.isMuted);
        if (mutedIcon) mutedIcon.classList.toggle('hidden', !this.isMuted);
      });
    }

    togglePiP() {
      if (document.pictureInPictureElement) {
        document.exitPictureInPicture().catch(() => {});
        return;
      }
      if (this.currentIndex === null) return;
      const slide = this.slides[this.currentIndex];
      const video = slide && slide.querySelector('[data-lightbox-video]');
      if (video && video.requestPictureInPicture) {
        video.requestPictureInPicture().catch(() => {});
      }
    }
  }

  customElements.define('dorahi-shoppable-stories', DorahiShoppableStories);
})();
